import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  addTemplateTab,
  removeTemplateTab,
  updateTemplateTabName,
  setActiveTabId,
  setTemplateDataCacheFull,
  setTabActiveView,
  setTabVmConfigValue,
  setIsFormVisible,
  setEditingTemplate,
  setSelectedFabricId,
  switchFabric,
  clearAllTabs,
  closeFabricTabs,
  clearWritingRulesNoChange,
} from '../../../store/genbiSlice';
import { exitProjectDataMode, openFabricEditFromProject } from '../../../store/dataFabricSlice';
import { clearProjectOrigin } from '../../../store/unstructuredThreadSlice';
import { TopNav, SideNav } from '../navigation/ConsoleNavigation';
import GetCookie from '../../utils/GetCookie';
import ErrorPopup from '../../utils/popup/ErrorPopup';
import ProcessingPopup from '../../utils/popup/ProcessingPopup';
import SuccessPopup from '../../utils/popup/SuccessPopup';
import WarningPopup from '../../utils/popup/WarningPopup';
import DeleteConfirmationModal from '../../utils/DeleteConfirmationModal';
import GenbiTemplateForm from './GenbiTemplateForm';
import GenbiTemplateList from './GenbiTemplateList';
import GenbiTemplateTabViewer from './GenbiTemplateTabViewer';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import authNavigate from '../../globalComponents/AuthNav';
import GenbiLandingComponent from './GenbiLandingComponent';
import ListVM from './ListVM';
import GenbiReportView from './GenbiReportViewer';
import useTemplateAutoRefresh from './useTemplateAutoRefresh';
import { usePermissions } from '../../utils/PermissionsContext';
import { PERMISSIONS } from '../../utils/permissionRoutes';


const GenbiDashboard = () => {
  const dispatch = useDispatch();
  const { can } = usePermissions();
  const navigate = useNavigate();

  const {
    openTemplateTabs,
    activeTabId,
    templateDataCache,
    tabActiveViews,
    tabVmConfig,
    isFormVisible,
    editingTemplate,
    selectedFabricId,
  } = useSelector((state) => state.genbi);

  // ── Local transient state (resets on mount — intentional) ─────────────────
  // category for side nav — event-driven, no persistence needed

  const [categoryFilter, setCategoryFilter] = useState(() => {
      const saved = sessionStorage.getItem('hta_selected_category');
      return saved ? [saved] : [];
  });
  
  const [showScrollArrows, setShowScrollArrows] = useState(false);
  const [isTemplateListCollapsed, setIsTemplateListCollapsed] = useState(false);
  // Loaded per tab action — no persistence needed
  const [fabricTreeData, setFabricTreeData] = useState([]);
  const [canCreateTemplate, setCanCreateTemplate] = useState(null);

  // Popup states — transient
  const [isProcessingPopupVisible, setIsProcessingPopupVisible] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  const [isErrorPopupVisible, setIsErrorPopupVisible] = useState(false);
  const [popupErrorMessage, setPopupErrorMessage] = useState('');
  const [isSuccessPopupVisible, setIsSuccessPopupVisible] = useState(false);
  const [popupSuccessMessage, setPopupSuccessMessage] = useState('');
  // Tracks if GenbiTemplateForm has unsaved changes
  const [isFormDirty, setIsFormDirty] = useState(false);

  // Delete confirmation modal states — transient
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);

  // ── Access re-check for restored tabs ─────────────────────────────────────
  // Open tabs and their loaded content live in Redux and survive navigation,
  // and a tab with cached content renders from it without asking the server.
  // So if the user's role on the project was removed meanwhile, coming back
  // showed a preset they can no longer see — while the sidebar, fetched fresh,
  // showed none. Tabs restored like that stay behind a loader until one
  // get-template-details call (role-checked on the project) confirms the
  // project is still readable. Once per project per visit: a preset opened
  // from the sidebar needs no check, since that list was just fetched.
  const [verifiedFabricIds, setVerifiedFabricIds] = useState(() => new Set());
  const accessCheckInFlightRef = useRef(null);
  const [accessLostInfo, setAccessLostInfo] = useState(null); // { projectName, leave }
  // The check's response can land after the tabs changed; it reads them here.
  const openTemplateTabsRef = useRef(openTemplateTabs);
  openTemplateTabsRef.current = openTemplateTabs;

  const markFabricVerified = (fabricId) => {
    const id = Number(fabricId);
    setVerifiedFabricIds(prev => (prev.has(id) ? prev : new Set(prev).add(id)));
  };

  const tabsFabricId = openTemplateTabs.length > 0 ? Number(openTemplateTabs[0].fabric_id) : null;
  const tabsAccessPending = Number.isFinite(tabsFabricId) && !verifiedFabricIds.has(tabsFabricId);

  // leave: the lost project is the one on screen, so after the notice the user
  // goes back to Projects. A check that lands after they switched elsewhere
  // only closes that project's parked tabs and leaves them where they are.
  const handleProjectAccessLost = (fabricId, projectName, leave) => {
    dispatch(closeFabricTabs(fabricId));
    if (leave && Number(sessionStorage.getItem('genbi_fabric_id')) === Number(fabricId)) {
      sessionStorage.removeItem('genbi_fabric_id');
    }
    setAccessLostInfo({ projectName: projectName || null, leave });
  };

  const closeAccessLostPopup = () => {
    const leave = accessLostInfo && accessLostInfo.leave;
    setAccessLostInfo(null);
    if (leave) authNavigate('/dashboard', 'iam', navigate);
  };

  useEffect(() => {
    if (!tabsAccessPending || accessCheckInFlightRef.current === tabsFabricId) return;
    const fabricId = tabsFabricId;
    const tab = openTemplateTabs.find(t => t.id === activeTabId) || openTemplateTabs[0];
    accessCheckInFlightRef.current = fabricId;
    axios.post(
      '/pceapi/genbi/get-template-details',
      { template_id: tab.id, fabric_id: fabricId, include_agent_responses: false },
      { headers: { Authorization: GetCookie('access_token') } }
    )
      .then((response) => {
        const data = response.data;
        if (data.status === 'NotAuthorized') {
          authNavigate('/login', 'iam', navigate);
          return;
        }
        if (data.status === 'failed' && data.code === 'ACCESS_DENIED') {
          const stillOnScreen = openTemplateTabsRef.current.some(t => Number(t.fabric_id) === fabricId);
          handleProjectAccessLost(fabricId, tab.fabric_name, stillOnScreen);
          return;
        }
        // Readable — or a failure that says nothing about access (a deleted
        // preset, a server error): keep the tabs. The backend still checks
        // every action, so failing open here exposes nothing new.
        markFabricVerified(fabricId);
      })
      .catch(() => markFabricVerified(fabricId))
      .finally(() => {
        if (accessCheckInFlightRef.current === fabricId) accessCheckInFlightRef.current = null;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabsAccessPending, tabsFabricId]);

  // Ref for TemplateList refresh
  const templateListRef = React.useRef();
  // Ref for GenbiTemplateForm — used to trigger save from outside
  const formRef = useRef(null);
  // Stores pending template to open after save-and-switch
  const pendingEditAfterSaveRef = useRef(null);

  // ── Wrapper: setTemplateDataCache supports functional updater pattern ──────
  // Child components (GenbiTemplateTabViewer) may call:
  //   setTemplateDataCache(prev => ({ ...prev, [id]: data }))
  // So we intercept and dispatch correctly.
  const handleSetTemplateDataCache = (updater) => {
    if (typeof updater === 'function') {
      dispatch(setTemplateDataCacheFull(updater(templateDataCache)));
    } else {
      dispatch(setTemplateDataCacheFull(updater));
    }
  };

    // Open the active project's data settings from the side rail.
  // Flags the origin in Redux so the project form shows the folder edit/create
  // affordances and knows which template tab to return to.
  const handleLinkData = async () => {
    const currentTab = openTemplateTabs.find((tab) => tab.id === activeTabId);
    if (!currentTab) return;

    const fabricId = currentTab.fabric_id;
    try {
      const token = GetCookie('access_token');
      const response = await axios.get(`/pceapi/fabrics/details/${fabricId}`, {
        headers: { Authorization: token },
      });
      const data = response.data;
      if (data?.data?.fabric) {
        dispatch(openFabricEditFromProject({
          fabricId,
          templateId: activeTabId,
          fabricData: data.data.fabric,
        }));
        authNavigate('/dashboard', 'iam', navigate);
        return;
      }
    } catch (e) {
      // Fall through to the original flow
    }
    dispatch(openFabricEditFromProject({
      fabricId,
      templateId: activeTabId,
    }));
    authNavigate('/dashboard', 'iam', navigate);
  };

  // ── Helpers ───────────────────────────────────────────────────────────────
  const refreshTemplateList = () => {
    if (templateListRef.current && templateListRef.current.fetchTemplates) {
      templateListRef.current.fetchTemplates();
    }
  };

  const getActiveViewForTab = (tabId) => tabActiveViews[tabId] || 'template';

  const setActiveViewForTab = (tabId, view) => {
    dispatch(setTabActiveView({ tabId, view }));
  };

  // ── Template handlers ─────────────────────────────────────────────────────
  const handleCreateTemplate = () => {
    if (selectedFabricId) {
      handleProcessingStart('Loading project data...');
    }
    dispatch(setEditingTemplate(null));
    dispatch(setIsFormVisible(true));
    setIsFormDirty(false);
  };

  const handleEditTemplate = async (template, fabric_id, fabric_name) => {
    handleProcessingStart(`Loading preset: ${template.template_name}`);

    try {
      const enhancedTemplate = {
        ...template,
        fabric_id,
        fabric_name,
        vm_config: template.vm_config || false,
        lang_id: template.lang_id || 1,
       };

      // Update selectedFabricId so sidebar stays filtered to this project
      sessionStorage.setItem('genbi_fabric_id', fabric_id);
      // Picked from the sidebar list, fetched this visit — no re-check needed.
      markFabricVerified(fabric_id);
      dispatch(switchFabric(fabric_id));
      dispatch(setEditingTemplate(enhancedTemplate));
      dispatch(setIsFormVisible(true));
      setIsFormDirty(false);
    } catch (error) {
      handleProcessingEnd();
      setPopupErrorMessage('Failed to load preset details');
      setIsErrorPopupVisible(true);
    }
  };

  const handleOpenTemplate = (template, fabric_id, fabric_name) => {
    const existingTab = openTemplateTabs.find((tab) => tab.id === template.id);

    // Always update selectedFabricId so sidebar stays filtered to this project
      sessionStorage.setItem('genbi_fabric_id', fabric_id);
      // Picked from the sidebar list, fetched this visit — no re-check needed.
      markFabricVerified(fabric_id);
      dispatch(switchFabric(fabric_id));
    if (!existingTab) {
      const newTab = {
        id: template.id,
        template_name: template.template_name,
        fabric_id,
        fabric_name,
        vm_config: template.vm_config || false,
        lang_id: template.lang_id || 1,
      };
      dispatch(addTemplateTab(newTab));
      dispatch(setActiveTabId(template.id));
      dispatch(setTabActiveView({ tabId: template.id, view: 'template' }));
      dispatch(setTabVmConfigValue({ tabId: template.id, config: template.vm_config === true }));
    } else {
      dispatch(clearWritingRulesNoChange(template.id));
      dispatch(setActiveTabId(template.id));
    }
  };

  const handleCloseTab = (templateId) => {
    // removeTemplateTab reducer handles: filter tabs, clear cache, clear views,
    // clear vmConfig, and auto-switch active tab — all in one dispatch.
    dispatch(removeTemplateTab(templateId));
};

  //  Delete handlers
  const handleDeleteRequest = (template, fabricId, fabricName) => {
    setTemplateToDelete({ ...template, fabric_id: fabricId, fabric_name: fabricName });
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    setShowDeleteConfirm(false);

    if (!templateToDelete) return;

    const isCurrentlyEditing = isFormVisible && editingTemplate?.id === templateToDelete.id;

    if (isCurrentlyEditing) {
      if (!window.confirm(`You are currently editing this preset. It will be deleted. Proceed?`)) {
        setTemplateToDelete(null);
        return;
      }
    }

    handleProcessingStart(`Deleting preset ${templateToDelete.template_name}`);

    try {
      const response = await axios.post(
        '/pceapi/genbi/delete-template',
        { template_id: templateToDelete.id, fabric_id: templateToDelete.fabric_id },
        { headers: { Authorization: GetCookie('access_token') } }
      );

      handleProcessingEnd();

      if (response.data.status === 'failed') {
        setPopupErrorMessage(response.data.message);
        setIsErrorPopupVisible(true);
      } else {
        if (isCurrentlyEditing) {
          dispatch(setIsFormVisible(false));
          dispatch(setEditingTemplate(null));
        }
        handleCloseTab(templateToDelete.id);
        setPopupSuccessMessage(response.data.message || 'Preset deleted successfully');
        setIsSuccessPopupVisible(true);
      }
    } catch (error) {
      handleProcessingEnd();
      setPopupErrorMessage('Failed to delete preset');
      setIsErrorPopupVisible(true);
    } finally {
      setTemplateToDelete(null);
    }
  };

  const handleDeleteTemplate = async (templateId, fabricId, templateName, isCurrentTemplate = false) => {
    if (isCurrentTemplate && editingTemplate && editingTemplate.id === templateId) {
      dispatch(setIsFormVisible(false));
      dispatch(setEditingTemplate(null));
    }
  };

  //Processing helpers
  const handleProcessingStart = (message) => {
    setProcessingMessage(message);
    setIsProcessingPopupVisible(true);
  };

  const handleProcessingEnd = () => {
    setIsProcessingPopupVisible(false);
    setProcessingMessage('');
  };

  const closeErrorPopup = () => setIsErrorPopupVisible(false);

  const closeSuccessPopup = () => {
    setIsSuccessPopupVisible(false);
    if (templateListRef.current && templateListRef.current.fetchTemplates) {
      templateListRef.current.fetchTemplates();
    }
  };

  //Fabric documents loader
  const loadFabricDocuments = async (fabricId) => {
    try {
      const response = await axios.post(
        '/pceapi/genbi/fabric_unstructured_data_tree',
        { fabric_id: fabricId, include_documents: true, page: 1, size: 25 },
        { headers: { Authorization: GetCookie('access_token') } }
      );
      if (response.data.status === 'success') {
        setFabricTreeData(response.data.data);
      }
    } catch (error) {
      console.error('Error loading project documents:', error);
    }
  };

  // Effects 

  // Category filter via custom event
useEffect(() => {
  const handleCategoryChange = (e) => {
    if (e.detail) {
      setCategoryFilter([e.detail]);
      sessionStorage.setItem('hta_selected_category', e.detail);
    } else {
      setCategoryFilter([]);
      sessionStorage.removeItem('hta_selected_category');
    }
    
    dispatch(clearAllTabs());
  };
  window.addEventListener('hta_category_change', handleCategoryChange);
  return () => window.removeEventListener('hta_category_change', handleCategoryChange);
}, []);

  // Safety net: being back on the template view means the "Link Data" trip is
  // over, so the project-data flags must not survive. The normal Update/Cancel
  // path already clears them — this covers browser back, session redirects, or
  // any other exit we haven't accounted for. Without it a stuck flag would
  // starve a later normal visit to Projects or Data Manager of its list fetch.
  useEffect(() => {
    dispatch(exitProjectDataMode());
    dispatch(clearProjectOrigin());
  }, []);

  // Read fabric_id from sessionStorage (Dashboard → Project card click / page
  // reload). Deliberately NOT removed here: Redux state resets on a hard
  // refresh, but sessionStorage survives it, so this is what lets a reload on
  // a project-scoped /hta-dossier land back on that same project instead of
  // falling through to every project. It only goes away on an explicit exit —
  // "Back to Project", or any sidebar navigation (ConsoleNavigation's
  // clearProjectDataMode) — not on being read.
  //
  // Fallback path only, now that DataFabricManagement's card click switches
  // the store before navigating: on that route this finds the ids already in
  // agreement and no-ops. It still earns its place for a hard refresh, where
  // the in-memory store is gone but sessionStorage survives — this is the one
  // thing that re-scopes the view to the project the user was on.
  useEffect(() => {
    const fabricId = sessionStorage.getItem('genbi_fabric_id');
    if (fabricId) {
      const numericFabricId = Number(fabricId);
      // eslint-disable-next-line eqeqeq
      if (selectedFabricId != numericFabricId) {
        dispatch(switchFabric(numericFabricId));
      }
    }
  }, []);

  // Restore category filter on remount — survives navigation to other pages
  useEffect(() => {
    const savedCategory = sessionStorage.getItem('hta_selected_category');
    if (savedCategory) {
      setCategoryFilter([savedCategory]);
      // NOTE: do NOT remove — DataFabricManagement also reads this on its mount
    }
  }, []);

  // Load fabric documents when switching to valuemessage view
  useEffect(() => {
    const currentTab = openTemplateTabs.find((tab) => tab.id === activeTabId);
    const activeView = getActiveViewForTab(activeTabId);
    if (currentTab?.fabric_id && activeView === 'valuemessage') {
      loadFabricDocuments(currentTab.fabric_id);
    }
  }, [activeTabId, tabActiveViews, openTemplateTabs]);

  // Sync tabVmConfig from templateDataCache when active tab changes
  useEffect(() => {
    if (activeTabId && templateDataCache[activeTabId]) {
      dispatch(setTabVmConfigValue({
        tabId: activeTabId,
        config: templateDataCache[activeTabId].vm_config === true,
      }));
    }
  }, [activeTabId, templateDataCache]);

  // Auto-scroll to active tab
  useEffect(() => {
    if (activeTabId && openTemplateTabs.length > 0) {
      setTimeout(() => {
        const activeButton = document.querySelector(`.tabbtn[data-tab-id="${activeTabId}"]`);
        if (activeButton) {
          activeButton.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
      }, 100);
    }
  }, [activeTabId, openTemplateTabs.length]);

  // Detect tab overflow for scroll arrows
  useEffect(() => {
    const checkOverflow = () => {
      const container = document.querySelector('.tabs-scroll-wrapper');
      if (container) {
        setShowScrollArrows(container.scrollWidth > container.clientWidth);
      }
    };
    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [openTemplateTabs]);

  //  Render tab content 
  const activeTemplate = openTemplateTabs.find(tab => tab.id === activeTabId);
  const autoRefresh = useTemplateAutoRefresh({
    templateId: activeTabId,
    fabricId: activeTemplate?.fabric_id,
    view: getActiveViewForTab(activeTabId),
    enabled: !tabsAccessPending && !isFormVisible && ['template', 'report'].includes(getActiveViewForTab(activeTabId)),
    onAccessLost: () => handleProjectAccessLost(activeTemplate?.fabric_id, activeTemplate?.fabric_name, true)
  });

  const renderTabContent = (tabId) => {
    const activeView = getActiveViewForTab(tabId);
    const currentTab = openTemplateTabs.find((tab) => tab.id === tabId);

    if (!currentTab) return null;

    switch (activeView) {
      case 'template':
        return (
          <GenbiTemplateTabViewer
            key={tabId}
            runtimeStatus={autoRefresh.runtime}
            registerRuntimeRefresh={autoRefresh.registerRefresh}
            onRuntimeWake={autoRefresh.wake}
            onRuntimeStale={autoRefresh.requestRefresh}
            templateId={tabId}
            templateDataCache={templateDataCache}
            fabricId={currentTab.fabric_id}
            templateName={currentTab.template_name}
            onTemplateNameSaved={(newName) => dispatch(updateTemplateTabName({ tabId, template_name: newName }))}
            setTemplateDataCache={handleSetTemplateDataCache}
            onError={(message) => {
              setPopupErrorMessage(message);
              setIsErrorPopupVisible(true);
            }}
            onProcessingStart={handleProcessingStart}
            onProcessingEnd={handleProcessingEnd}
            onRefreshTemplateList={refreshTemplateList}
            vmConfig={tabVmConfig[tabId]}
            langId={currentTab.lang_id || 1}
            onAccessLost={() => handleProjectAccessLost(currentTab.fabric_id, currentTab.fabric_name, true)}
          />
        );
      case 'valuemessage':
        return (
          <div>
            <ListVM
              selectedTemplate={tabId}
              fabric_id={currentTab.fabric_id}
              fabricTreeData={fabricTreeData}
            />
          </div>
        );
      case 'report':
        return (
          <GenbiReportView
            key={tabId}
            registerRuntimeRefresh={autoRefresh.registerRefresh}
            templateId={tabId}
            onError={(message) => {
              setPopupErrorMessage(message);
              setIsErrorPopupVisible(true);
            }}
            onProcessingStart={handleProcessingStart}
            onProcessingEnd={handleProcessingEnd}
            onRefreshTemplateList={refreshTemplateList}
          />
        );
      default:
        return null;
    }
  };

  // Render
  return (
    <div className="main-window">
      <TopNav />
      <div className="container-fluid">
        <div className="row">
          <SideNav />
          <main className="col-12 col-md-9 col-lg-10 ms-sm-auto console-main-window">
            <div>
              {/* Header */}
              <div className="ev-card-2 dashboard-header">
                {!isFormVisible ? (
                  canCreateTemplate ? (
                    <button
                      className="ev-btn-yellow-fill-round ev-bold-reg"
                      onClick={handleCreateTemplate}
                    >
                      <i className="bi bi-plus-circle me-1"></i> Import Preset
                    </button>
                  ) : (
                    /* Header is a 3-column flex row; the placeholder keeps the
                       title centred when the button is not offered. */
                    <div></div>
                  )
                ) : (
                  <button 
                    className="ev-btn-lightblue-fill-round ev-bold-reg" 
                    onClick={() => {
                      dispatch(setIsFormVisible(false));
                      dispatch(setEditingTemplate(null));
                    }}
                  >
                    Preset Navigation
                  </button>
                )}
                <p className="ev-med-p-dark mb-0 ev-bold-reg">HTA Dossier Studio</p>
                <button
                  className="ev-btn-lightblue-fill-round ev-bold-reg"
                  onClick={() => { sessionStorage.removeItem('genbi_fabric_id'); authNavigate('/dashboard', 'iam', navigate); }}
                >
                  <i className="bi bi-arrow-left me-1"></i> Back to Project
                </button>
              </div>

              <div>
                {/* Sidebar - Always show template list */}
                <GenbiTemplateList
                  ref={templateListRef}
                  categoryFilter={categoryFilter}
                  selectedFabricId={selectedFabricId}
                  onClearSelectedFabric={() => dispatch(setSelectedFabricId(null))}
                  onEditTemplate={handleEditTemplate}
                  onOpenTemplate={handleOpenTemplate}
                  onRefresh={() => {}}
                  onDeleteTemplate={handleDeleteTemplate}
                  onDeleteRequest={handleDeleteRequest}
                  openTemplateIds={openTemplateTabs.map((tab) => tab.id)}
                  onError={(message) => {
                    setPopupErrorMessage(message);
                    setIsErrorPopupVisible(true);
                  }}
                  onCanCreateChange={setCanCreateTemplate}
                  isFormVisible={isFormVisible}
                  editingTemplateId={editingTemplate?.id || null}
                  editingTemplateName={editingTemplate?.template_name || ''}
                  isFormDirty={isFormDirty}
                  onSaveTemplate={(pendingEdit) => {
                    pendingEditAfterSaveRef.current = pendingEdit || null;
                    formRef.current?.submitForm();
                  }}
                  onProcessingStart={handleProcessingStart}
                  onProcessingEnd={handleProcessingEnd}
                  isCollapsed={isTemplateListCollapsed}
                  onToggleCollapse={() => setIsTemplateListCollapsed(prev => !prev)}
                />

                {/* Main content */}
                <div className={`inner-window1 ${isTemplateListCollapsed ? 'genbi-template-content--expanded' : ''}`}>
                  {isFormVisible ? (
                    <>
                      {console.log('tabId passed:', editingTemplate?.id, 'editingTemplate:', editingTemplate)}
                      <GenbiTemplateForm
                        ref={formRef}
                        editingTemplate={editingTemplate}
                        initialFabricId={selectedFabricId}
                        selectedTemplate={editingTemplate?.id || null}
                        onDirtyChange={setIsFormDirty}
                        onSuccess={(message, updatedLangId, savedTemplateName) => {
                            if (editingTemplate?.id) {
                              // Sync tab name immediately
                              if (savedTemplateName) {
                                dispatch(updateTemplateTabName({ tabId: editingTemplate.id, template_name: savedTemplateName }));
                              }
                              handleSetTemplateDataCache(prev => {
                                const newCache = { ...prev };
                                delete newCache[editingTemplate.id];
                                return newCache;
                              });
                            }
                          setIsFormDirty(false);
                          setPopupSuccessMessage(message);
                          setIsSuccessPopupVisible(true);
                          const pending = pendingEditAfterSaveRef.current;
                          pendingEditAfterSaveRef.current = null;
                          if (pending) {
                            dispatch(setIsFormVisible(false));
                            dispatch(setEditingTemplate(null));
                            setTimeout(() => {
                              handleEditTemplate(pending.template, pending.fabricId, pending.fabricName);
                            }, 50);
                          } else {
                            if (updatedLangId && editingTemplate) {
                              dispatch(setEditingTemplate({ ...editingTemplate, lang_id: updatedLangId }));
                            }
                            dispatch(setIsFormVisible(false));
                            dispatch(setEditingTemplate(null));
                          }
                        }}
                        onError={(message) => {
                          setPopupErrorMessage(message);
                          setIsErrorPopupVisible(true);
                        }}
                        onCancel={() => {
                          dispatch(setIsFormVisible(false));
                          dispatch(setEditingTemplate(null));
                        }}
                        onProcessingStart={handleProcessingStart}
                        onProcessingEnd={handleProcessingEnd}
                      />
                    </>
                  ) : tabsAccessPending ? (
                    // Restored tabs wait here until the project is confirmed
                    // readable — see the access re-check above.
                    <div className="d-flex align-items-center justify-content-center py-5">
                      <span className="spinner-border spinner-border-sm text-primary me-2" role="status" aria-hidden="true"></span>
                      <span className="ev-small-p-dark">Checking your access to this project…</span>
                    </div>
                  ) : openTemplateTabs.length > 0 ? (
                    <div>
                      {/* Tabs */}
                      <div className="nav-tabs-container mt-0 mb-1">
                        {/* Left Arrow */}
                        {showScrollArrows && (
                          <button
                            className="ev-btn-lightblue-fill-round ev-icon-btn-padding-small"
                            onClick={() => {
                              const currentIndex = openTemplateTabs.findIndex((tab) => tab.id === activeTabId);
                              if (currentIndex > 0) {
                                dispatch(setActiveTabId(openTemplateTabs[currentIndex - 1].id));
                              }
                            }}
                            disabled={openTemplateTabs.length <= 1}
                          >
                            <i className="bi bi-chevron-left"></i>
                          </button>
                        )}

                        {/* Scrollable tabs wrapper */}
                        <div className="tabs-scroll-wrapper">
                          {openTemplateTabs.map((tab) => (
                            <button
                              key={tab.id}
                              data-tab-id={tab.id}
                              className={`tabbtn tabbtn-round ${activeTabId === tab.id ? 'tabbtn-active' : 'tabbtn-inactive'}`}
                              onClick={() => dispatch(setActiveTabId(tab.id))}
                              title={tab.template_name}
                            >
                              {tab.template_name.length > 15
                                ? tab.template_name.substring(0, 15) + '...' 
                                : tab.template_name} 
                              <button 
                                className="tabbtn-close ms-2"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCloseTab(tab.id);
                                }}
                              >
                                <i className="bi bi-x"></i>
                              </button>
                            </button>
                          ))}
                        </div>

                        {/* Right Arrow */}
                        {showScrollArrows && (
                          <button
                            className="ev-btn-lightblue-fill-round ev-icon-btn-padding-small"
                            onClick={() => {
                              const currentIndex = openTemplateTabs.findIndex((tab) => tab.id === activeTabId);
                              if (currentIndex < openTemplateTabs.length - 1) {
                                dispatch(setActiveTabId(openTemplateTabs[currentIndex + 1].id));
                              }
                            }}
                            disabled={openTemplateTabs.length <= 1}
                          >
                            <i className="bi bi-chevron-right"></i>
                          </button>
                        )}
                      </div>


                      {/* Tab content with side navigation */}
                      {activeTabId && (
                        <div className="row ev-card-4 dashboard-right-main w-100 border-0 m-0 ev-padding-small">
                          {/* Side navigation pane */}
                          <div className="col-1 ps-0">
                            <div className="ev-card-4 dashboard-right-main-sidebar w-100 ev-radius-small">
                              <div className="d-flex flex-column align-items-center">
                                <button
                                  className={`ev-btn-lightblue-fill-round ev-small-p-blue dashboard-right-main-sidebar-btn avatar-tooltip text-center my-2 ${getActiveViewForTab(activeTabId) === 'template' ? 'active' : ''}`}
                                  data-tooltip="HTA Dossier Studio"
                                  onClick={() => setActiveViewForTab(activeTabId, 'template')}
                                >
                                  <i className="bi bi-kanban-fill"></i>
                                </button>
                                
                                {/* Only show Value Messages if vm_config is true */}
                                {tabVmConfig[activeTabId] === true && (
                                  <button
                                    className={`ev-btn-lightblue-fill-round ev-small-p-blue dashboard-right-main-sidebar-btn avatar-tooltip text-center my-2 ${getActiveViewForTab(activeTabId) === 'valuemessage' ? 'active' : ''}`}
                                    data-tooltip="Value Messages" 
                                    onClick={() => setActiveViewForTab(activeTabId, 'valuemessage')}
                                  >
                                    <i className="bi bi-chat-dots-fill"></i>
                                  </button>
                                )}

                                <button
                                  className={`ev-btn-lightblue-fill-round ev-small-p-blue dashboard-right-main-sidebar-btn avatar-tooltip text-center my-2 ${getActiveViewForTab(activeTabId) === 'report' ? 'active' : ''}`}
                                  data-tooltip="Manage Reports"
                                  onClick={() => setActiveViewForTab(activeTabId, 'report')}
                                >
                                  <i className="bi bi-file-text-fill"></i>
                                </button>

                                <button
                                  className="ev-btn-lightblue-fill-round ev-small-p-blue dashboard-right-main-sidebar-btn avatar-tooltip text-center my-2"
                                  data-tooltip="Link Data"
                                  onClick={handleLinkData}
                                >
                                  <i className="bi bi-folder2-open"></i>
                                </button>
                              </div>
                            </div>
                          </div>

                          {/* Main content area */}
                          <div className="col-11 ps-0">
                            <div className="w-100 position-relative dashboard-right-main-contentarea">
                              {renderTabContent(activeTabId)}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <GenbiLandingComponent canCreatePreset={canCreateTemplate} />
                  )}
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        show={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setTemplateToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        objectName="preset"
        itemName={templateToDelete?.template_name || ''}
        bodyContent={
          templateToDelete && (
            <div>
              <div className="mb-2">
                <strong>Project:</strong> {templateToDelete.fabric_name}
              </div>
              <div className="mb-2">
                <strong>Agents:</strong> {templateToDelete.agent_count || 0}
              </div>
              <div className="mb-3">
                <strong>Sections:</strong> {templateToDelete.section_count || 0}
              </div>
              {editingTemplate?.id === templateToDelete.id && (
                <div className="alert alert-warning" role="alert">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  <strong>Warning:</strong> You are currently editing this preset. Deleting it will close the form.
                </div>
              )}
              <p className="text-danger mb-0">
                This will permanently delete the preset and all its sections and agents. This action cannot be undone.
              </p>
            </div>
          )
        }
      />

      {/* Popups */}
      {isProcessingPopupVisible && <ProcessingPopup message={processingMessage} />}
      {isErrorPopupVisible && <ErrorPopup onClose={closeErrorPopup} message={popupErrorMessage} />}
      {isSuccessPopupVisible && <SuccessPopup onClose={closeSuccessPopup} message={popupSuccessMessage} />}
      {accessLostInfo && (
        <WarningPopup
          title="Project access removed"
          message={accessLostInfo.projectName
            ? `You no longer have access to the project "${accessLostInfo.projectName}", so its open presets were closed.`
            : 'You no longer have access to this project, so its open presets were closed.'}
          onClose={closeAccessLostPopup}
        />
      )}
    </div>
  );
};

export default GenbiDashboard;
